import { useEffect, useRef, useMemo } from 'react';
import KeywordSearchMessageList from './KeywordSearchMessageList';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import { useSearchQuery } from 'redux-stores/KeywordSearch/keywordSearchPrimaryLevelApi';
import { useRTKQueryInfiniteResults } from 'common/hooks/useRTKQueryInfiniteResults';

const classes = BEM.with('KeywordSearchSideBar');

const { setIsFetching } = actions;

type MobxProps = {
  currentOrganizationId: string;
  trackingInboxSearchGainsight: (searchLocation: string) => void;
};

const KeywordSearchFirstLevel = ({
  currentOrganizationId,
  trackingInboxSearchGainsight,
}: MobxProps) => {
  const dispatch = useAppDispatch();
  const keywordSearchRef = useRef<HTMLDivElement>(null);
  const prevKeywordSearchTerm = useRef<string>('');

  const { selectedConversationId, keywordSearchTerm } = useAppSelector(
    (state) => state.keywordSearch
  );

  const queryArguments = {
    searchValue: keywordSearchTerm,
    orgId: currentOrganizationId,
  };

  const shouldSkipQuery = keywordSearchTerm.trim().length < 2;
  const { results, isFetching, response } = useRTKQueryInfiniteResults(
    useSearchQuery,
    {
      args: queryArguments,
    },
    { ref: keywordSearchRef, skipQuery: shouldSkipQuery }
  );
  const { continuation } = response?.metadata || {};

  const effectiveResults = useMemo(() => {
    return shouldSkipQuery ? [] : results;
  }, [shouldSkipQuery, results]);

  useEffect(() => {
    if (prevKeywordSearchTerm.current === keywordSearchTerm) {
      const hasResults = (results && results?.length > 0) || false;
      if (!isFetching && hasResults) {
        trackingInboxSearchGainsight('inbox search');
      }
    } else {
      prevKeywordSearchTerm.current = keywordSearchTerm;
    }
  }, [isFetching, keywordSearchTerm, results, trackingInboxSearchGainsight]);

  useEffect(() => {
    if (isFetching && continuation === undefined) {
      dispatch(setIsFetching(true));
    } else {
      dispatch(setIsFetching(false));
    }
  }, [isFetching, dispatch, continuation]);

  const hasSelectedConversation = Boolean(selectedConversationId && selectedConversationId.trim());

  return (
    <div
      className={classes('results-container', {
        block: !hasSelectedConversation,
        none: hasSelectedConversation,
      })}
      ref={keywordSearchRef}
    >
      {effectiveResults?.map(({ entity }, index) => (
        <KeywordSearchMessageList
          key={`keyword-search-message-list-${index}`}
          value={{
            entity,
            organization_id: currentOrganizationId,
            type: 'message',
          }}
        />
      ))}
    </div>
  );
};

export default mobxInjectSelect<{}, MobxProps>({
  messengerStore: ['currentOrganizationId'],
  trackerStore: ['trackingInboxSearchGainsight'],
})(KeywordSearchFirstLevel);
